import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Box,
  
  Typography,
  Autocomplete,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Search as SearchIcon, LocationOn as LocationIcon } from '@mui/icons-material';

const MASSACHUSETTS_CITIES = [
  'Somerville',
  'Cambridge',
  'Boston',
  'Lexington',
  'Arlington',
  'Medford',
  'Malden',
  'Everett',
  'Chelsea',
  'Revere',
  'Brookline',
  'Newton',
  'Watertown',
  'Belmont',
  'Winchester',
  'Woburn',
  'Burlington',
  'Waltham',
  'Quincy',
  'Dedham'
];

interface AddressSearchProps {
  onSubmit: (address: string, city: string) => void;
  loading?: boolean;
}

const AddressSearch: React.FC<AddressSearchProps> = ({ onSubmit, loading = false }) => {
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('Somerville');
  const [errors, setErrors] = useState<{ address?: string; city?: string }>({});

  const validateForm = () => {
    const newErrors: { address?: string; city?: string } = {};
    
    if (!address.trim()) {
      newErrors.address = 'Please enter a street address';
    } else if (address.length < 5) {
      newErrors.address = 'Please enter a valid street address';
    }
    
    if (!city) {
      newErrors.city = 'Please select a city';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(address.trim(), city);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
    if (errors.address) {
      setErrors({ ...errors, address: undefined });
    }
  };

  return (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <LocationIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Enter Property Address
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Get comprehensive feasibility analysis for any Massachusetts property
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Street Address"
              placeholder="123 Main Street"
              value={address}
              onChange={handleAddressChange}
              error={!!errors.address}
              helperText={errors.address}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Autocomplete
              value={city}
              onChange={(_, newValue) => {
                setCity(newValue || 'Somerville');
                if (errors.city) {
                  setErrors({ ...errors, city: undefined });
                }
              }}
              options={MASSACHUSETTS_CITIES}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  error={!!errors.city}
                  helperText={errors.city}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              {loading ? 'Analyzing Property...' : 'Analyze Property'}
            </Button>
          </Grid>
        </Grid>
      </form>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Analysis includes zoning compliance, development options, market data, and financial projections
        </Typography>
      </Box>
    </Paper>
  );
};

export default AddressSearch;